import React, { useEffect, useRef, useState } from 'react';

export default function AudioPlayerWithSpeed({ src, compact = false }) {
  const SPEED_OPTIONS = [0.5, 0.75, 1.0, 1.25, 1.5];
  const [rate, setRate] = useState(1.0);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, [rate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    audio.load();
  }, [src]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.warn('Audio play blocked:', err);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = Number(e.target.value);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const rawSeekValue = Math.min(currentTime, safeDuration || 0);
  const seekValue = safeDuration > 0
    ? (rawSeekValue < 0.02 ? 0 : (safeDuration - rawSeekValue < 0.05 ? safeDuration : rawSeekValue))
    : 0;
  const seekProgress = safeDuration > 0 ? `${Math.min(100, (seekValue / safeDuration) * 100)}%` : '0%';
  const handleCycleRate = () => {
    const idx = SPEED_OPTIONS.indexOf(rate);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    setRate(next);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '0.35rem' : '0.6rem', marginTop: compact ? 0 : '0.2rem', width: compact ? undefined : '100%', justifyContent: compact ? 'flex-start' : 'center' }}>
      {error ? (
        <div style={{ color: 'red', fontSize: '0.8rem' }}>Audio error: {error}</div>
      ) : (
        <>
          <audio
            ref={audioRef}
            src={src}
            preload="metadata"
            onError={(e) => {
              const code = e.target?.error?.code;
              const codes = { 1: 'aborted', 2: 'network', 3: 'decode', 4: 'format not supported' };
              const msg = codes[code] || `unknown (code ${code})`;
              console.error('Audio playback error:', msg, 'src:', src);
              setError(msg);
            }}
            onLoadedMetadata={() => {
              const audio = audioRef.current;
              if (!audio) return;
              setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
              audio.playbackRate = rate;
            }}
            onTimeUpdate={() => {
              const audio = audioRef.current;
              if (!audio) return;
              setCurrentTime(audio.currentTime || 0);
            }}
            onEnded={() => {
              setIsPlaying(false);
              setCurrentTime(0);
            }}
            style={{ display: 'none' }}
          />
          <button
            className="btn-subtle"
            onClick={togglePlay}
            style={{ minWidth: compact ? '52px' : '56px', padding: compact ? '4px 8px' : '5px 10px' }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <input
            className="audio-seek-slider"
            type="range"
            min={0}
            max={safeDuration || 0}
            step={0.01}
            value={seekValue}
            onChange={handleSeek}
            style={{
              width: compact ? '90px' : '170px',
              '--seek-progress': seekProgress
            }}
            aria-label="Seek recording"
          />
        </>
      )}
      <button
        className="btn-subtle"
        aria-label="Cycle playback speed"
        onClick={handleCycleRate}
        title={`Speed ${rate}x (tap to change)`}
        style={{
          fontSize: '0.8rem',
          minWidth: compact ? '44px' : '52px',
          padding: compact ? '4px 8px' : '5px 10px',
          marginLeft: 0,
          lineHeight: 1.1
        }}
      >
        {rate}x
      </button>
    </div>
  );
}
